import React, { forwardRef } from 'react';

import { HStack, Icon, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

interface SidebarButtonProps extends React.PropsWithChildren {
  label: string;
}

const hoverAndFocusStyles = {
  backgroundColor: 'primitives.white.100alpha10',
  borderColor: 'border.weak',
};

// NOTE: This component is unstyled to avoid pulling in the full Samba Button styles
// which add padding, font styles, and more that we don't need here.
//
// If there is enough demand for a Samba "unstyled" button, we may propose promoting
// it into Samba directly.
//
const UnstyledButton = forwardRef<HTMLButtonElement, React.PropsWithChildren>(
  ({ children, ...props }, forwardedRef) => (
    <button
      ref={forwardedRef}
      type="button"
      className={css({
        all: 'unset',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderRadius: 'medium',
        borderColor: 'border.strong',
        transition: 'border-color 0.1s ease-in-out',
        _focusVisible: {
          ...hoverAndFocusStyles,
          outlineWidth: 'thick',
          outlineColor: 'border.focused',
          outlineStyle: 'solid',
          outlineOffset: '1px',
        },
        _hover: hoverAndFocusStyles,
      })}
      {...props}
    >
      {children}
    </button>
  )
);

const SidebarButton = forwardRef<HTMLButtonElement, SidebarButtonProps>(
  ({ label, children, ...props }, forwardedRef) => {
    return (
      <UnstyledButton ref={forwardedRef} aria-label={label} {...props}>
        <HStack
          css={{
            alignItems: 'center',
            mx: 'space.150',
            my: 'space.100',
          }}
        >
          <VStack css={{ flexGrow: 1 }}>{children}</VStack>
          <Icon css={{ d: 'block' }} size="small" color="icon.onPrimary" name="Ellipses" />
        </HStack>
      </UnstyledButton>
    );
  }
);

export default SidebarButton;
